import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSocket } from '../hooks/useSocket';
import { useAuth } from '../hooks/useAuth';
import { AlertCircle, RefreshCw, Banknote, X, CheckCircle2, Loader2 } from 'lucide-react';
import { API_BASE_URL } from './config/api';

/**
 * Listens for pos_preload:update events globally — works regardless of which
 * screen the cashier is on (OrdersPage, DeliveryWithMap, Dashboard, etc.).
 *
 * The in-page OrderPreloadBanner still exists for in-context viewing inside
 * the order card. This toast is the safety net so the cashier never misses a
 * busy/failed event because the relevant card wasn't rendered yet.
 */

interface PreloadEvent {
  order_id: number;
  short_code?: string | null;
  status: 'sending' | 'success' | 'failed' | 'switched_to_cash';
  reason?: string;
  message?: string;
}

const AUTO_DISMISS_MS = 4000;

const GlobalPosPreloadToast: React.FC = () => {
  const { t } = useTranslation('orders');
  const { socket } = useSocket();
  const { store } = useAuth();
  const [events, setEvents] = useState<Record<number, PreloadEvent>>({});
  const [submittingOrderId, setSubmittingOrderId] = useState<number | null>(null);

  useEffect(() => {
    if (!socket) return;
    const handler = (payload: PreloadEvent) => {
      if (!payload?.order_id) return;
      setEvents(prev => ({ ...prev, [payload.order_id]: payload }));
      // Auto-dismiss success / cash-switch after a few seconds.
      if (payload.status === 'success' || payload.status === 'switched_to_cash') {
        setTimeout(() => {
          setEvents(prev => {
            const next = { ...prev };
            delete next[payload.order_id];
            return next;
          });
        }, AUTO_DISMISS_MS);
      }
    };
    socket.on('pos_preload:update', handler);
    return () => { socket.off('pos_preload:update', handler); };
  }, [socket]);

  const dismiss = useCallback((orderId: number) => {
    setEvents(prev => {
      const next = { ...prev };
      delete next[orderId];
      return next;
    });
  }, []);

  const callApi = async (orderId: number, path: string) => {
    const r = await fetch(`${API_BASE_URL}/api/delivery/${store?.id}/orders/${orderId}/${path}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('xentrix_token')}`,
      },
    });
    if (!r.ok) {
      const err = await r.json().catch(() => ({}));
      throw new Error(err.message || err.error || `HTTP ${r.status}`);
    }
    return r.json();
  };

  const handleRetry = async (orderId: number) => {
    setSubmittingOrderId(orderId);
    try {
      await callApi(orderId, 'preload-retry');
      setEvents(prev => ({ ...prev, [orderId]: { ...prev[orderId], status: 'sending' } }));
    } catch (err: any) {
      alert(err.message || t('preloadBanner.retryFailed', { defaultValue: 'Η νέα δοκιμή απέτυχε' }));
    } finally {
      setSubmittingOrderId(null);
    }
  };

  const handleSwitchToCash = async (orderId: number) => {
    setSubmittingOrderId(orderId);
    try {
      await callApi(orderId, 'switch-to-cash');
      dismiss(orderId);
    } catch (err: any) {
      alert(err.message || t('preloadBanner.switchFailed', { defaultValue: 'Η αλλαγή σε μετρητά απέτυχε' }));
    } finally {
      setSubmittingOrderId(null);
    }
  };

  const items = Object.values(events);
  if (items.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 max-w-md">
      {items.map(evt => {
        const orderLabel = evt.short_code ? `#${evt.short_code}` : `#${evt.order_id}`;
        const isBusy = evt.status === 'failed' && evt.reason === 'busy';
        const isFailed = evt.status === 'failed';
        const submitting = submittingOrderId === evt.order_id;

        const variant =
          evt.status === 'success' ? 'bg-emerald-100 dark:bg-emerald-900/90 border-emerald-500/60 text-emerald-100' :
          evt.status === 'switched_to_cash' ? 'bg-emerald-100 dark:bg-emerald-900/90 border-emerald-500/60 text-emerald-100' :
          isBusy ? 'bg-amber-100 dark:bg-amber-900/90 border-amber-500/60 text-amber-100' :
          isFailed ? 'bg-red-100 dark:bg-red-900/90 border-red-500/60 text-red-100' :
          'bg-blue-100 dark:bg-blue-900/90 border-blue-500/60 text-blue-100';

        return (
          <div
            key={evt.order_id}
            className={`flex items-start gap-2 px-4 py-3 rounded-xl border shadow-2xl backdrop-blur-sm text-sm font-medium ${variant}`}
          >
            <div className="shrink-0 mt-0.5">
              {evt.status === 'sending' && <Loader2 className="h-4 w-4 animate-spin" />}
              {evt.status === 'success' && <CheckCircle2 className="h-4 w-4" />}
              {evt.status === 'switched_to_cash' && <Banknote className="h-4 w-4" />}
              {isFailed && <AlertCircle className="h-4 w-4" />}
            </div>

            <div className="flex-1 min-w-0">
              <div className="font-bold mb-0.5">
                {t('preloadBanner.orderLabel', { defaultValue: 'Παραγγελία' })} {orderLabel}
              </div>
              <div className="text-xs opacity-90">
                {evt.status === 'sending' && t('preloadBanner.sending', { defaultValue: 'Αποστολή στο POS τερματικό…' })}
                {evt.status === 'success' && t('preloadBanner.success', { defaultValue: 'POS έτοιμο, παρέδωσε το τερματικό στον πελάτη' })}
                {evt.status === 'switched_to_cash' && t('preloadBanner.switchedToCash', { defaultValue: 'Η παραγγελία θα πληρωθεί με μετρητά' })}
                {isBusy && t('preloadBanner.busy', { defaultValue: 'Το POS είναι απασχολημένο. Πάτησε «Νέα δοκιμή» όταν είσαι έτοιμος.' })}
                {isFailed && !isBusy && t('preloadBanner.failed', { defaultValue: 'Το POS δεν δέχτηκε τη συναλλαγή' })}
              </div>

              {isFailed && (
                <div className="flex gap-1.5 mt-2">
                  <button
                    onClick={() => handleRetry(evt.order_id)}
                    disabled={submitting}
                    className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-t1 text-xs font-semibold transition-all active:scale-95 disabled:opacity-50 ${
                      isBusy ? 'bg-amber-700 hover:bg-amber-600' : 'bg-red-700 hover:bg-red-600'
                    }`}
                  >
                    <RefreshCw className={`h-3.5 w-3.5 ${submitting ? 'animate-spin' : ''}`} />
                    {t('preloadBanner.retry', { defaultValue: 'Νέα δοκιμή' })}
                  </button>
                  <button
                    onClick={() => handleSwitchToCash(evt.order_id)}
                    disabled={submitting}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-semibold transition-all active:scale-95 disabled:opacity-50"
                  >
                    <Banknote className="h-3.5 w-3.5" />
                    {t('preloadBanner.switchToCash', { defaultValue: 'Μετρητά' })}
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => dismiss(evt.order_id)}
              className="shrink-0 p-1 rounded-md hover:bg-white/10 transition-colors"
              aria-label="dismiss"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
};

export default GlobalPosPreloadToast;
